import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";
import Slide from "@material-ui/core/Slide";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ContactModal from "../ContactModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Grid, ListItemText, MenuItem, Select } from "@material-ui/core";
import { toast } from "react-toastify";
import { Facebook, Instagram, WhatsApp } from "@material-ui/icons";
import ShowTicketOpen from "../ShowTicketOpenModal";
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const useStyles = makeStyles((theme) => ({
  dialog: {
    borderRadius: 8,
    backgroundColor: "#f5f5f5",
  },
  dialogTitle: {
    backgroundColor: "#3f51b5",
    color: "white",
    textAlign: "center",
    padding: theme.spacing(2),
  },
  dialogContent: {
    padding: theme.spacing(3),
  },
  dialogActions: {
    padding: theme.spacing(2),
    justifyContent: "center",
  },
  buttonCancel: {
    color: "white",
    backgroundColor: "#f44336",
    "&:hover": {
      backgroundColor: "#d32f2f",
    },
  },
  buttonConfirm: {
    color: "white",
    backgroundColor: "#4caf50",
    "&:hover": {
      backgroundColor: "#388e3c",
    },
  },
  online: {
    fontSize: 11,
    color: "#25d366",
  },
  offline: {
    fontSize: 11,
    color: "#e1306c",
  },
}));

const filter = createFilterOptions({
  trim: true,
});

const NewTicketModal = ({ modalOpen, onClose, initialContact }) => {
  const classes = useStyles();
  const [options, setOptions] = useState([]);
  const [channelFilter, setChannelFilter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState("");
  const [selectedWhatsapp, setSelectedWhatsapp] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [newContact, setNewContact] = useState({});
  const [whatsapps, setWhatsapps] = useState([]);
  const [users, setUsers] = useState([]);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const { user } = useContext(AuthContext);
  const { companyId, whatsappId } = user;

  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState("");
  const [queueTicketOpen, setQueueTicketOpen] = useState("");

  useEffect(() => {
    if (initialContact?.id !== undefined) {
      setOptions([initialContact]);
      setSelectedContact(initialContact);
    }
  }, [initialContact]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        api
          .get(`/whatsapp`, { params: { companyId, session: 0 } })
          .then(({ data }) => setWhatsapps(data));
      };

      const fetchUsers = async () => {
        try {
          const { data } = await api.get("/users/", {
            params: { searchParam: "" },
          });
          setUsers(data.users || []);
        } catch (err) {
          console.log("Error loading users");
        }
      };

      // Se não tem WhatsApp selecionado, pegar o primeiro disponível
      if (whatsappId !== null && whatsappId !== undefined) {
        setSelectedWhatsapp(whatsappId);
      } else {
        // Buscar WhatsApps primeiro para depois selecionar o primeiro
        api.get(`/whatsapp`, { params: { companyId, session: 0 } })
          .then(({ data }) => {
            if (data && data.length > 0) {
              setSelectedWhatsapp(data[0].id);
            }
          })
          .catch(err => console.log("Error loading whatsapps"));
      }

      if (user.queues.length === 1) {
        setSelectedQueue(user.queues[0].id);
      }

      setSelectedUser(user.id);

      fetchContacts();
      fetchUsers();
      setLoading(false);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [selectedContact, channelFilter]);

  useEffect(() => {
    if (!modalOpen || searchParam.length < 3) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("contacts", {
            params: { searchParam },
          });
          setOptions(data.contacts);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, modalOpen]);

  const IconChannel = (channel) => {
    switch (channel) {
      case "facebook":
        return <Facebook style={{ color: "#3b5998", verticalAlign: "middle" }} />;
      case "instagram":
        return <Instagram style={{ color: "#e1306c", verticalAlign: "middle" }} />;
      default:
        return <WhatsApp style={{ color: "#25d366", verticalAlign: "middle" }} />;
    }
  };

  const handleClose = () => {
    onClose();
    setSearchParam("");
    setOpenAlert(false);
    setUserTicketOpen("");
    setQueueTicketOpen("");
    setSelectedContact(null);
  };

  const handleCloseAlert = () => {
    setOpenAlert(false);
    setLoading(false);
    setOpenAlert(false);
    setUserTicketOpen("");
    setQueueTicketOpen("");
  };

  const handleSaveTicket = async (contactId) => {
    if (!contactId) return;
    if (selectedQueue === "") {
      toast.error("Selecione uma fila");
      return;
    }

    setLoading(true);
    try {
      const queueId = selectedQueue !== "" ? selectedQueue : null;
      const whatsappId = selectedWhatsapp !== "" ? selectedWhatsapp : null;
      const assignUserId = selectedUser !== "" ? selectedUser : user.id;
      const { data: ticket } = await api.post("/tickets", {
        contactId: contactId,
        queueId,
        whatsappId,
        userId: assignUserId,
        status: "open",
      });

      onClose(ticket);
    } catch (err) {
      let ticket = null;
      const errorPayload = err?.response?.data?.error;

      if (typeof errorPayload === "string") {
        try {
          ticket = JSON.parse(errorPayload);
        } catch (parseError) {
          toastError(err);
          setLoading(false);
          return;
        }
      } else if (typeof errorPayload === "object" && errorPayload !== null) {
        ticket = errorPayload;
      }

      if (ticket?.userId !== user?.id) {
        setOpenAlert(true);
        setUserTicketOpen(ticket?.user?.name);
        setQueueTicketOpen(ticket?.queue?.name);
      } else {
        setOpenAlert(false);
        setUserTicketOpen("");
        setQueueTicketOpen("");
        setLoading(false);
        onClose(ticket);
      }
    }
    setLoading(false);
  };

  const handleSelectOption = (e, newValue) => {
    if (newValue?.number) {
      setSelectedContact(newValue);
    } else if (newValue?.name) {
      // Quem digita um numero nao deveria ter que digitar de novo no cadastro.
      const digitos = String(newValue.name).replace(/\D/g, "");
      const ehNumero = digitos.length >= 8 && digitos.length === String(newValue.name).trim().replace(/[\s()+-]/g, "").length;
      setNewContact(ehNumero ? { name: digitos, number: digitos } : { name: newValue.name });
      setContactModalOpen(true);
    }
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
  };

  const handleAddNewContactTicket = (contact) => {
    setSelectedContact(contact);
  };

  const createAddContactOption = (filterOptions, params) => {
    const filtered = filter(filterOptions, params);
    if (params.inputValue !== "" && !loading && searchParam.length >= 3) {
      filtered.push({
        name: `${params.inputValue}`,
      });
    }
    return filtered;
  };

  const renderOption = (option) => {
    if (option.number) {
      return (
        <>
          {IconChannel(option.channel)}
          <Typography component="span" style={{ fontSize: 14, marginLeft: "10px", display: "inline-flex", alignItems: "center", lineHeight: "2" }}>
            {option.name} - {option.number}
          </Typography>
        </>
      );
    } else {
      return `${i18n.t("newTicketModal.add")} ${option.name}`;
    }
  };

  const renderOptionLabel = (option) => {
    if (option.number) {
      return `${option.name} - ${option.number}`;
    } else {
      return `${option.name}`;
    }
  };

  const renderContactAutocomplete = () => {
    if (!initialContact || initialContact === undefined || initialContact.id === undefined) {
      return (
        <Grid xs={12} item>
          <Autocomplete
            fullWidth
            options={options}
            loading={loading}
            clearOnBlur
            autoHighlight
            freeSolo
            clearOnEscape
            getOptionLabel={renderOptionLabel}
            renderOption={renderOption}
            filterOptions={createAddContactOption}
            onChange={(e, newValue) => {
              setChannelFilter(newValue ? newValue.channel : "whatsapp");
              handleSelectOption(e, newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={i18n.t("newTicketModal.fieldLabel")}
                variant="outlined"
                autoFocus
                onChange={(e) => setSearchParam(e.target.value)}
                onKeyPress={(e) => {
                  if (loading || !selectedContact) return;
                  else if (e.key === "Enter") {
                    handleSaveTicket(selectedContact.id);
                  }
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        </Grid>
      );
    }
    return null;
  };

  return (
    <Dialog 
      open={modalOpen} 
      onClose={handleClose} 
      TransitionComponent={Transition}
      classes={{ paper: classes.dialog }}
      disableEnforceFocus={contactModalOpen || openAlert}
    >
      <DialogTitle id="form-dialog-title" className={classes.dialogTitle}>
        {i18n.t("newTicketModal.title")}
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        <Grid container spacing={2}>
          {renderContactAutocomplete()}
          <Grid xs={12} item>
            <Typography variant="caption" style={{ display: "block", marginBottom: 4, color: "#667781", fontWeight: 600 }}>
              Fila
            </Typography>
            <Select
              required
              fullWidth
              displayEmpty
              variant="outlined"
              value={selectedQueue}
              onChange={(e) => setSelectedQueue(e.target.value)}
              MenuProps={{
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                getContentAnchorEl: null,
              }}
              renderValue={() => {
                if (selectedQueue === "") {
                  return "Selecione uma fila";
                }
                const queue = user.queues.find((q) => q.id === selectedQueue);
                return queue.name;
              }}
            >
              {user.queues?.length > 0 &&
                user.queues.map((queue, key) => (
                  <MenuItem dense key={key} value={queue.id}>
                    <ListItemText primary={queue.name} />
                  </MenuItem>
                ))}
            </Select>
          </Grid>
          <Grid xs={12} item>
            <Typography variant="caption" style={{ display: "block", marginBottom: 4, color: "#667781", fontWeight: 600 }}>
              Atendente
            </Typography>
            <Select
              required
              fullWidth
              displayEmpty
              variant="outlined"
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              MenuProps={{
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                getContentAnchorEl: null,
              }}
              renderValue={() => {
                if (selectedUser === "") {
                  return "Selecione um Atendente";
                }
                const u = users.find((u) => u.id === selectedUser);
                return u ? u.name : user.name;
              }}
            >
              {users?.length > 0 &&
                users.map((u, key) => (
                  <MenuItem dense key={key} value={u.id}>
                    <ListItemText primary={u.name} />
                  </MenuItem>
                ))}
            </Select>
          </Grid>
          <Grid xs={12} item>
            <Typography variant="caption" style={{ display: "block", marginBottom: 4, color: "#667781", fontWeight: 600 }}>
              Conexão
            </Typography>
            <Select
              required
              fullWidth
              displayEmpty
              variant="outlined"
              value={selectedWhatsapp}
              onChange={(e) => setSelectedWhatsapp(e.target.value)}
              MenuProps={{
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                getContentAnchorEl: null,
              }}
              renderValue={() => {
                if (selectedWhatsapp === "") {
                  return "Selecione uma Conexão";
                }
                const whatsapp = whatsapps.find((w) => w.id === selectedWhatsapp);
                return whatsapp?.name;
              }}
            >
              {whatsapps?.length > 0 &&
                whatsapps.map((whatsapp, key) => (
                  <MenuItem dense key={key} value={whatsapp.id}>
                    <ListItemText
                      primary={
                        <>
                          {IconChannel(whatsapp.channel)}
                          <Typography component="span" style={{ fontSize: 14, marginLeft: "10px", display: "inline-flex", alignItems: "center", lineHeight: "2" }}>
                            {whatsapp.name} &nbsp; <p className={whatsapp.status === 'CONNECTED' ? classes.online : classes.offline}>({whatsapp.status})</p>
                          </Typography>
                        </>
                      }
                    />
                  </MenuItem>
                ))}
            </Select>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={handleClose}
            style={{
            fontSize: "12px",
            color: "white",
            backgroundColor: "#db6565",
            boxShadow: "none",
            borderRadius: 0
            }}
          disabled={loading}
          startIcon={<CancelIcon />}
          variant="contained"
        >
          {i18n.t("newTicketModal.buttons.cancel")}
        </Button>
        <ButtonWithSpinner
          startIcon={<CheckIcon />}
          variant="contained"
          type="button"
          disabled={!selectedContact}
          onClick={() => handleSaveTicket(selectedContact.id)}
            style={{
            fontSize: "12px",
            color: "white",
            backgroundColor: "#437db5",
            boxShadow: "none",
            borderRadius: 0
            }}
          loading={loading}
        >
          {i18n.t("newTicketModal.buttons.ok")}
        </ButtonWithSpinner>
      </DialogActions>
      {contactModalOpen && (
        <ContactModal
          open={contactModalOpen}
          initialValues={newContact}
          onClose={handleCloseContactModal}
          onSave={handleAddNewContactTicket}
        />
      )}
      {openAlert && (
        <ShowTicketOpen
          isOpen={openAlert}
          handleClose={handleCloseAlert}
          user={userTicketOpen}
          queue={queueTicketOpen}
        />
      )}
    </Dialog>
  );
};

export default NewTicketModal;